import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Server, Shield, Network, Terminal, CheckCircle, Code, Cpu, GitBranch, Twitter, Github, Linkedin, Heart } from 'lucide-react';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      icon: Server,
      title: "Real Environments",
      description: "Practice in actual Linux containers with real server configurations",
      color: "text-blue-400"
    },
    {
      icon: Shield,
      title: "Safe Learning",
      description: "Experiment freely in isolated environments without consequences",
      color: "text-green-400"
    },
    {
      icon: Network,
      title: "Progressive Path",
      description: "Start with basics and advance to complex system debugging",
      color: "text-purple-400"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "DevOps Engineer",
      company: "TechCorp",
      image: "/avatars/sarah.jpg",
      text: "OpsQuest helped me transition from development to DevOps. The hands-on challenges were exactly what I needed."
    },
    {
      name: "Michael Rodriguez",
      role: "System Administrator",
      company: "CloudScale",
      image: "/avatars/michael.jpg",
      text: "The real-world scenarios in OpsQuest prepared me for actual production issues. Invaluable training platform!"
    },
    {
      name: "Emma Thompson",
      role: "Cloud Engineer",
      company: "InfraStack",
      image: "/avatars/emma.jpg",
      text: "From basic SSH to complex Kubernetes debugging, OpsQuest's progressive learning path is outstanding."
    }
  ];

  const stats = [
    { 
      label: "Active Learners", 
      value: "10,000+",
      icon: Code,
      color: "text-blue-400"
    },
    { 
      label: "Challenges Completed", 
      value: "50,000+",
      icon: Cpu,
      color: "text-green-400"
    },
    { 
      label: "Success Rate", 
      value: "94%",
      icon: GitBranch,
      color: "text-purple-400"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -right-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-radial from-blue-500/5 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 pt-20 pb-32 relative">
        {/* Hero Section */}
        <div className={`text-center mb-24 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-8">
            <div className="relative">
              <Terminal className="w-20 h-20 text-blue-400" />
              <div className="absolute inset-0 bg-blue-400/20 rounded-lg blur-xl animate-pulse-slow"></div>
            </div>
          </div>
          <h1 className="text-7xl font-bold mb-8 leading-tight">
            Master DevOps Through{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient">
              Real Challenges
            </span>
          </h1>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Learn DevOps by solving real-world server problems in isolated environments.
            Build practical skills with hands-on experience that mirrors production scenarios.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <button 
              onClick={() => navigate('/signup')}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <span className="relative text-xl font-semibold text-white">Start Free Trial</span>
            </button>
            <button 
              onClick={() => navigate('/login')}
              className="group px-8 py-4 bg-gray-800/50 rounded-lg border border-gray-700 transition-all duration-300 hover:bg-gray-800/70 hover:border-gray-600"
            >
              <span className="text-xl font-semibold text-gray-300 group-hover:text-white">Sign In</span>
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="group relative p-8 card bg-gray-800/30 backdrop-blur border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative flex flex-col items-center">
                <stat.icon className={`w-12 h-12 ${stat.color} mb-4`} />
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-center text-white mb-20">
            Why Choose{' '}
            <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              OpsQuest
            </span>
            ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group relative p-8 card bg-gray-800/30 backdrop-blur border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 hover:transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <feature.icon className={`w-16 h-16 ${feature.color} mx-auto mb-6 transform group-hover:scale-110 transition-transform duration-300`} />
                  <h3 className="text-2xl font-semibold text-white mb-4 text-center">{feature.title}</h3>
                  <p className="text-gray-400 text-center leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Free Trial Section */}
        <div className="relative card bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-16 mb-32 border border-gray-700/50 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 animate-pulse-slow"></div>
          <div className="relative text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-8">
              Start Your Free Trial Today
            </h2>
            <p className="text-2xl text-gray-300 mb-12">
              Get unlimited access to all features for 14 days. No credit card required.
            </p>
            <ul className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-12">
              {[
                "Access to all challenges",
                "Real Linux environments",
                "Detailed solutions",
                "Progress tracking",
                "Community support",
                "Certificate of completion"
              ].map((feature, index) => (
                <li key={index} className="flex items-center space-x-4 text-lg">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={() => navigate('/signup')}
              className="group relative px-10 py-5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <span className="relative text-2xl font-semibold text-white">Start Free Trial</span>
            </button>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mb-32">
          <h2 className="text-5xl font-bold text-center text-white mb-20">
            What Our Users Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="group relative card bg-gray-800/30 backdrop-blur p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 hover:transform hover:-translate-y-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mr-4"></div>
                    <div>
                      <div className="font-semibold text-white text-lg">{testimonial.name}</div>
                      <div className="text-gray-400">{testimonial.role}</div>
                      <div className="text-gray-400">{testimonial.company}</div>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">{testimonial.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-5xl font-bold text-white mb-8">
            Ready to Start Your DevOps Journey?
          </h2>
          <p className="text-2xl text-gray-300 mb-12">
            Join thousands of engineers who are mastering DevOps with OpsQuest
          </p>
          <button 
            onClick={() => navigate('/signup')}
            className="group relative px-10 py-5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg overflow-hidden transition-all duration-300 hover:scale-105"
          >
            <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <span className="relative text-2xl font-semibold text-white">Start Free Trial</span>
          </button>
        </div>
      </div>

      {/* Add the footer section before the closing div */}
      <footer className="relative mt-32 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Terminal className="w-8 h-8 text-blue-400" />
                <h3 className="text-2xl font-bold text-white">OpsQuest</h3>
              </div>
              <p className="text-gray-400">
                Master DevOps through hands-on challenges in real environments.
              </p>
              <div className="flex space-x-4">
                <a href="https://twitter.com/opsquest" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://github.com/opsquest" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Github className="w-5 h-5" />
                </a>
                <a href="https://linkedin.com/company/opsquest" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/challenges" className="text-gray-400 hover:text-white transition-colors">Challenges</a>
                </li>
                <li>
                  <a href="/pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</a>
                </li>
                <li>
                  <a href="/enterprise" className="text-gray-400 hover:text-white transition-colors">Enterprise</a>
                </li>
                <li>
                  <a href="/roadmap" className="text-gray-400 hover:text-white transition-colors">Roadmap</a>
                </li>
                <li>
                  <a href="/changelog" className="text-gray-400 hover:text-white transition-colors">Changelog</a>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/docs" className="text-gray-400 hover:text-white transition-colors">Documentation</a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</a>
                </li>
                <li>
                  <a href="/community" className="text-gray-400 hover:text-white transition-colors">Community</a>
                </li>
                <li>
                  <a href="/discord" className="text-gray-400 hover:text-white transition-colors">Discord</a>
                </li>
                <li>
                  <a href="/status" className="text-gray-400 hover:text-white transition-colors">System Status</a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/about" className="text-gray-400 hover:text-white transition-colors">About Us</a>
                </li>
                <li>
                  <a href="/careers" className="text-gray-400 hover:text-white transition-colors">Careers</a>
                </li>
                <li>
                  <a href="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</a>
                </li>
                <li>
                  <a href="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</a>
                </li>
                <li>
                  <a href="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</a>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div className="border-t border-gray-800 pt-8 pb-12">
            <div className="max-w-2xl mx-auto text-center">
              <h4 className="text-lg font-semibold text-white mb-4">Stay Updated</h4>
              <p className="text-gray-400 mb-6">
                Get the latest DevOps tips and resources straight to your inbox.
              </p>
              <form className="flex flex-col sm:flex-row gap-4 justify-center">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-blue-500 text-white w-full sm:w-auto"
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 flex items-center space-x-2">
                <span>© 2024 OpsQuest.</span>
                <span className="hidden md:inline">|</span>
                <span className="flex items-center">
                  Made with <Heart className="w-4 h-4 text-red-500 mx-1" /> by DevOps enthusiasts
                </span>
              </div>
              <div className="flex items-center space-x-6">
                <a href="/sitemap" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Sitemap
                </a>
                <a href="/accessibility" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Accessibility
                </a>
                <a href="/cookies" className="text-gray-400 hover:text-white transition-colors text-sm">
                  Cookie Settings
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}; 